import { toast } from "@arcadeai/design-system/components/ui/molecules/toast";
import { useCallback } from "react";
import type { FieldPath, FieldValues, UseFormReturn } from "react-hook-form";
import { type HttpError, isHttpError, isValidationError } from "./http-error";

// Rejections a form has taken responsibility for by putting them on its inputs.
// The mutation error handler reads this to stay quiet for those and still toast
// one nobody took — most dashboard mutations are not wired to a form at all.
const formOwnedErrors = new WeakSet<HttpError>();

export function isFormOwnedError(error: unknown): boolean {
  return isHttpError(error) && formOwnedErrors.has(error);
}

/**
 * Takes responsibility for a rejection without a form to pin it to, for a field
 * that reads the error off its own request and renders it itself. Without this
 * the handler would toast what that field is already showing.
 */
export function claimFormError(error: unknown): void {
  if (isValidationError(error)) {
    formOwnedErrors.add(error);
  }
}

type ServerFieldError = {
  field: string;
  message: string;
};

function getServerFieldErrors(error: HttpError): ServerFieldError[] {
  if (
    typeof error.data !== "object" ||
    error.data === null ||
    !("field_errors" in error.data) ||
    !Array.isArray(error.data.field_errors)
  ) {
    return [];
  }

  return error.data.field_errors.filter(
    (fieldError): fieldError is ServerFieldError =>
      typeof fieldError === "object" &&
      fieldError !== null &&
      "field" in fieldError &&
      typeof fieldError.field === "string" &&
      "message" in fieldError &&
      typeof fieldError.message === "string"
  );
}

const ARRAY_INDEX = /\[(\d+)\]/g;
// Every trailing index, so `allowed_tools[0]` and `matrix[0][1]` both resolve to
// the field that renders them rather than to an element of it.
const TRAILING_ARRAY_INDEX = /(?:\.\d+)+$/;

const toFormFieldPath = (path: string): string =>
  path.replace(ARRAY_INDEX, ".$1").replace(TRAILING_ARRAY_INDEX, "");

// Whether this form has the field a server path names. Server paths are runtime
// strings from another service's schema, so one can name an input this form
// doesn't have — `setError` would accept it and nothing would ever render it.
function holdsPath(values: unknown, path: string): boolean {
  let node = values;
  for (const segment of path.split(".")) {
    if (typeof node !== "object" || node === null || !(segment in node)) {
      return false;
    }
    node = Reflect.get(node, segment);
  }
  return true;
}

type ServerErrorForm<T extends FieldValues> = Pick<
  UseFormReturn<T>,
  "setError" | "getValues"
>;

type ApplyServerErrorOptions = {
  /**
   * Rewrites a server field path into this form's field layout before
   * matching, for forms whose fields sit at a different nesting level than
   * the wire payload (e.g. a config sub-object flattened into the form).
   */
  mapPath?: (path: string) => string;
};

/**
 * Routes a rejected submission to whichever surface the user will actually see:
 * a named input gets the message on the input itself, and anything left over
 * gets a toast. A validation error that reaches no form at all is toasted by the
 * mutation error handler in `query-client.ts`.
 */
export function applyServerErrors<T extends FieldValues>(
  form: ServerErrorForm<T>,
  error: unknown,
  options?: ApplyServerErrorOptions
): void {
  if (!isValidationError(error)) {
    return;
  }

  const fieldErrors = getServerFieldErrors(error);
  const values = form.getValues();
  const unplaced: string[] = [];

  for (const fieldError of fieldErrors) {
    const serverField = options?.mapPath
      ? options.mapPath(fieldError.field)
      : fieldError.field;
    const path = toFormFieldPath(serverField);
    if (holdsPath(values, path)) {
      // biome-ignore lint/plugin/no-type-assertion: server field paths are runtime values from the API contract, checked against this form's shape by holdsPath.
      form.setError(path as FieldPath<T>, {
        type: "server",
        message: fieldError.message,
      });
      continue;
    }
    unplaced.push(fieldError.message);
  }

  if (fieldErrors.length === 0) {
    // Nothing here to put on an input — the mutation error handler toasts it,
    // wired form or not, so toasting here would say it twice.
    return;
  }

  formOwnedErrors.add(error);

  // Claimed above, so the handler is now quiet for this error: any message that
  // didn't reach an input has to be reported here or it is never seen.
  if (unplaced.length > 0) {
    toast.error(unplaced.join("; "));
  }
}

export function useServerFormErrors<T extends FieldValues>(
  form: ServerErrorForm<T>,
  options?: ApplyServerErrorOptions
): (error: unknown) => void {
  return useCallback(
    (error: unknown) => applyServerErrors(form, error, options),
    [form, options]
  );
}
